import React, { useMemo, useCallback, useState } from "react";

import * as yup from "yup";

import Select from "react-select";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm, Controller } from "react-hook-form";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";
import Table from "react-bootstrap/Table";

import Input from "../../../../components/Input";
import { ToggleFeatureButton } from "../Shelly/ShellyControllers";

import {
  getClientFeatureQuery,
  QUERY_TUYA,
  // QUERY_TUYA_AVAILABLE_DEVICES,
} from "../../../../config/graphql/query";
import {
  MUTATION_CREATE_TUYA,
  MUTATION_UPDATE_TUYA,
  MUTATION_DELETE_TUYA,
  MUTATION_CREATE_TUYA_CONTROLLER,
  MUTATION_DELETE_TUYA_CONTROLLER,
  TOGGLE_CLIENT_FEATURE,
  MUTATION_START_TUYA_MONITORING,
  MUTATION_STOP_TUYA_MONITORING,
  MUTATION_SYNC_TUYA_CONTROLLER_MEMBERS,
} from "../../../../config/graphql/mutation";

import useHasRole from "../../../../lib/hooks/useHasRole";
import { useClientId } from "../hooks/useClient";
import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

type TuyaDataCenter = "eu" | "us" | "cn" | "in";

const DATA_CENTER_OPTIONS: { value: TuyaDataCenter; label: string }[] = [
  { value: "eu", label: "Europe (EU)" },
  { value: "us", label: "United States (US)" },
  { value: "cn", label: "China (CN)" },
  { value: "in", label: "India (IN)" },
];

const CreateTuyaControllerModal = ({
  show,
  setShow,
  tuyaId,
  clientId,
}: {
  show: boolean;
  setShow: (nextValue?: any) => void;
  tuyaId: string;
  clientId: string;
}) => {
  const { t } = useTranslation(["client", "common"]);

  const [deviceId, setDeviceId] = useState<string>();
  const [outsideMethods, setOutsideMethods] = useState<TuyaAccessMethod[]>([]);
  const [insideMethods, setInsideMethods] = useState<TuyaAccessMethod[]>([]);

  const accessOptions: { value: TuyaAccessMethod; label: string }[] = [
    { value: "auto", label: "AUTO" },
    { value: "manual", label: "MANUAL" },
  ];

  // const { data, loading } = useQuery<{
  //   tuyaAvailableDevices: {
  //     deviceId: string;
  //     deviceName: string;
  //   }[];
  // }>(QUERY_TUYA_AVAILABLE_DEVICES, {
  //   context: {
  //     headers: {
  //       ClientId: clientId,
  //     },
  //   },
  //   skip: !show,
  // });

  // const deviceOptions = useMemo(
  //   () =>
  //     (data?.tuyaAvailableDevices ?? []).map((d) => ({
  //       value: d.deviceId,
  //       label: `${d.deviceName} (${d.deviceId})`,
  //     })),
  //   [data],
  // );

  const [onCreate] = useMutation(MUTATION_CREATE_TUYA_CONTROLLER, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: {
      input: {
        deviceId,
        tuya: tuyaId,
        accessMethods: {
          outside: outsideMethods,
          inside: insideMethods,
        },
      },
    },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setShow(false);

    return onCreate()
      .then(() => {
        toast.success<string>(t("client:client.tuya.toast.controllerCreated"));
      })
      .catch(() => {
        toast.error<string>(
          t("client:client.tuya.toast.controllerCreatedError"),
        );
      });
  };

  return (
    <Modal
      show={show}
      size="lg"
      onHide={setShow}
      centered
      aria-labelledby="contained-modal-title-vcenter"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("client:client.tuya.controllers.modalCreate.title")}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={onSubmit}>
        <Modal.Body>
          <label htmlFor="deviceId">
            {t("client:client.tuya.controllers.modalCreate.controller")}
          </label>
          {/* <Select
            inputId="deviceId"
            isLoading={loading}
            options={deviceOptions}
            value={deviceOptions.find((o) => o.value === deviceId) ?? null}
            onChange={(option) => setDeviceId(option?.value ?? null)}
            placeholder={t("common:select")}
            styles={reactSelectCustomStyles(false)}
            isClearable
          /> */}
          <input
            id="deviceId"
            className="form-control mb-3"
            value={deviceId}
            onChange={({ target: { value } }) =>
              setDeviceId(value === "" ? undefined : value)
            }
          />
          <div className="form-group">
            <label>
              {t(
                "client:client.tuya.controllers.modalCreate.accessMethod.outside.title",
              )}
            </label>
            <Select
              isMulti
              options={accessOptions}
              value={accessOptions.filter((o) =>
                outsideMethods.includes(o.value),
              )}
              onChange={(selected) =>
                setOutsideMethods(selected.map((s) => s.value))
              }
              placeholder={t("common:select")}
            />
            <small className="form-text text-muted">
              <ul className="mb-0">
                <li>
                  {t(
                    "client:client.tuya.controllers.modalCreate.accessMethod.outside.auto",
                  )}
                </li>
                <li>
                  {t(
                    "client:client.tuya.controllers.modalCreate.accessMethod.outside.manual",
                  )}
                </li>
              </ul>
            </small>
          </div>

          <div className="form-group">
            <label>
              {t(
                "client:client.tuya.controllers.modalCreate.accessMethod.inside.title",
              )}
            </label>
            <Select
              isMulti
              options={accessOptions}
              value={accessOptions.filter((o) =>
                insideMethods.includes(o.value),
              )}
              onChange={(selected) =>
                setInsideMethods(selected.map((s) => s.value))
              }
              placeholder={t("common:select")}
            />
            <small className="form-text text-muted">
              <ul className="mb-0">
                <li>
                  {t(
                    "client:client.tuya.controllers.modalCreate.accessMethod.inside.auto",
                  )}
                </li>
                <li>
                  {t(
                    "client:client.tuya.controllers.modalCreate.accessMethod.inside.manual",
                  )}
                </li>
              </ul>
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" type="submit">
            {t("client:client.tuya.controllers.modalCreate.createBtn")}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

const TableRow = ({
  item,
  clientId,
  index,
}: {
  item: ITuyaController;
  clientId: string;
  index: number;
}) => {
  const { id, deviceId, deviceName, product } = item;

  const [visible, setVisible] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);

  const [syncControllerMembers, { loading: syncing }] = useMutation(
    MUTATION_SYNC_TUYA_CONTROLLER_MEMBERS,
  );

  const [onDelete] = useMutation(MUTATION_DELETE_TUYA_CONTROLLER, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: { id },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onSyncControllerMembers = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      try {
        await syncControllerMembers({
          variables: {
            controllerId: id,
          },
        });

        toast.success<string>(t("client:client.tuya.toast.syncMembers"));
      } catch (error: any) {
        toast.error<string>(t("client:client.tuya.toast.syncMembersError"));
      }
    },
    [id, syncControllerMembers, t],
  );

  const onBeforeDelete = () => {
    setVisible(false);

    return onDelete()
      .then(() => {
        toast.success<string>(t("client:client.tuya.toast.controllerDeleted"));
      })
      .catch(() => {
        toast.error<string>(
          t("client:client.tuya.toast.controllerDeletedError"),
        );
      });
  };

  return (
    <>
      <tr>
        <th scope="row">{index + 1}</th>
        <td>{deviceName}</td>
        <td>{deviceId}</td>
        {/* <td>{deviceType}</td> */}
        <td>{product}</td>
        <td>{item.accessMethods?.outside?.join(", ").toUpperCase()}</td>
        <td>{item.accessMethods?.inside?.join(", ").toUpperCase()}</td>
        <td className="text-nowrap">
          <div className="d-flex justify-content-around">
            <button
              onClick={onSyncControllerMembers}
              className="btn btn-outline-primary"
              style={{ width: "9rem" }}
              disabled={syncing}
            >
              {syncing
                ? t("client:client.tuya.button.syncingMembers")
                : t("client:client.tuya.button.syncMembers")}
            </button>
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
          </div>
        </td>
      </tr>
      <Modal
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-center">
            {t("client:client.tuya.controllers.modalDelete.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.tuya.controllers.modalDelete.body")}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface FieldValues {
  dataCenter: TuyaDataCenter;
  accessId: string;
  accessSecret: string;
}

const Tuya = React.memo(() => {
  const [show, setShow] = useToggle(false);

  const [showCreateController, setShowCreateController] = useToggle(false);

  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const schema = useMemo(
    () =>
      yup.object().shape({
        dataCenter: yup
          .string()
          .oneOf(["eu", "us", "cn", "in"])
          .required(t("client:client.tuya.yup.dataCenter")),
        accessId: yup.string().required(t("client:client.tuya.yup.accessId")),
        accessSecret: yup
          .string()
          .required(t("client:client.tuya.yup.accessSecret")),
      }),
    [t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
    defaultValues: {
      dataCenter: "eu",
      accessId: "",
      accessSecret: "",
    },
  });

  const { data: tuyaData } = useQuery<{
    tuya: ITuya;
  }>(QUERY_TUYA, {
    skip: !clientId,
    onCompleted: ({ tuya }) => methods.reset(tuya),
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const tuyaId = useMemo(() => tuyaData?.tuya?.id, [tuyaData]);

  const websocketStatus = useMemo(
    () => tuyaData?.tuya?.websocketStatus ?? "stopped",
    [tuyaData],
  );

  const tuyaControllers = useMemo(
    () => tuyaData?.tuya?.controllers ?? [],
    [tuyaData],
  );

  const [onUpdate] = useMutation(MUTATION_UPDATE_TUYA, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onCreate] = useMutation(MUTATION_CREATE_TUYA, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onDelete] = useMutation(MUTATION_DELETE_TUYA, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onStartMonitoring] = useMutation(MUTATION_START_TUYA_MONITORING, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: { id: tuyaId },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const [onStopMonitoring] = useMutation(MUTATION_STOP_TUYA_MONITORING, {
    refetchQueries: [
      {
        query: QUERY_TUYA,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    variables: { id: tuyaId },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onBeforeDelete = useCallback(() => {
    setShow(false);
    return onDelete({ variables: { id: tuyaId } })
      .then(() => {
        methods.reset({
          dataCenter: "eu",
          accessId: "",
          accessSecret: "",
        });
        toast.success<string>(t("client:client.tuya.toast.deleted"));
      })
      .catch((error) => {
        toast.error<string>(
          error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
        );
      });
  }, [onDelete, t, setShow, tuyaId]);

  const onBeforeStartMonitoring = async () => {
    onStartMonitoring()
      .then(() => {
        toast.success<string>(t("client:client.tuya.toast.startMonitoring"));
      })
      .catch(() => {
        toast.error<string>(t("client:client.tuya.toast.startMonitoringError"));
      });
  };

  const onBeforeStopMonitoring = async () => {
    onStopMonitoring()
      .then(() => {
        toast.success<string>(t("client:client.tuya.toast.stopMonitoring"));
      })
      .catch(() => {
        toast.error<string>(t("client:client.tuya.toast.stopMonitoringError"));
      });
  };

  const onSubmit = useCallback(
    async (values: FieldValues) => {
      if (tuyaId) {
        return onUpdate({
          variables: { input: { id: tuyaId, ...values } },
        })
          .then(({ data: { updateTuya } }) => {
            methods.reset(updateTuya);
          })
          .then(() => {
            toast.success<string>(t("client:client.tuya.toast.updated"));
          })
          .catch((error) => {
            toast.error<string>(
              error?.networkError?.result?.errors?.[0]?.message ??
                error?.message,
            );
          });
      }

      return onCreate({ variables: { input: values } })
        .then(() => {
          toast.success<string>(t("client:client.tuya.toast.created"));
        })
        .catch((error) => {
          toast.error<string>(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [tuyaId],
  );

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-3 col-md-6 col-sm-12">
            <h5 className="mb-3">{t("client:client.tuya.form.title")}</h5>
            <div className="form-group">
              <label htmlFor="dataCenter">
                {t("client:client.tuya.form.dataCenter")}
              </label>
              <Controller
                name="dataCenter"
                control={methods.control}
                render={({ field }) => (
                  <Select
                    inputId="dataCenter"
                    options={DATA_CENTER_OPTIONS}
                    value={
                      DATA_CENTER_OPTIONS.find(
                        (o) => o.value === field.value,
                      ) ?? null
                    }
                    onChange={(option) => field.onChange(option?.value ?? "eu")}
                    placeholder={t("common:select")}
                    styles={reactSelectCustomStyles(
                      !!methods.formState.errors.dataCenter,
                    )}
                  />
                )}
              />
              {methods.formState.errors.dataCenter && (
                <small className="text-danger">
                  {methods.formState.errors.dataCenter.message}
                </small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="accessId">
                {t("client:client.tuya.form.accessId")}
              </label>
              <Input name="accessId" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="accessSecret">
                {t("client:client.tuya.form.accessSecret")}
              </label>
              <Input name="accessSecret" className="form-control" />
            </div>
            {!!tuyaId && (
              <div className="pb-2">
                <div className="form-group">
                  <label htmlFor="websocketStatus">
                    {t("client:client.tuya.form.websocketStatus")}
                  </label>
                  <input
                    id="websocketStatus"
                    className="form-control"
                    style={{
                      backgroundColor:
                        websocketStatus === "active" ? "#eaf7ee" : "#fdeeee",
                      borderColor:
                        websocketStatus === "active" ? "#b7e4c7" : "#f5c2c7",
                    }}
                    value={
                      websocketStatus === "active"
                        ? "CONNECTED"
                        : "DISCONNECTED"
                    }
                    disabled
                    readOnly
                  />
                </div>
                <div className="w-100 d-flex">
                  <Button
                    className="w-50"
                    variant="success"
                    onClick={onBeforeStartMonitoring}
                  >
                    {t("client:client.tuya.button.startMonitoring")}
                  </Button>
                  <Button
                    variant="danger"
                    className="w-50 ml-2 text-white"
                    onClick={onBeforeStopMonitoring}
                  >
                    {t("client:client.tuya.button.stopMonitoring")}
                  </Button>
                </div>
              </div>
            )}
            <div className="mt-4">
              <input
                disabled={methods.formState.isSubmitting}
                type="submit"
                style={{ width: "7.3rem" }}
                className="btn btn-primary mr-2"
                value={t(
                  `client:client.tuya.button.${!!tuyaId ? "update" : "create"}`,
                )}
              />
              {!!tuyaId && (
                <button
                  style={{ width: "7.3rem" }}
                  onClick={(e) => {
                    e.preventDefault();

                    setShow(true);
                  }}
                  className="btn btn-danger"
                >
                  {t("common:delete")}
                </button>
              )}
            </div>
          </div>

          {!!tuyaId && (
            <div className="col-lg-9 col-md-6 col-sm-12">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>
                  {t("client:client.tuya.controllers.title", {
                    count: 2,
                  })}
                </h5>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={(e) => {
                    e.preventDefault();

                    setShowCreateController(true);
                  }}
                >
                  {t("client:client.tuya.controllers.button.add")}
                </button>
              </div>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">
                      {t("client:client.tuya.controllers.table.th.deviceName")}
                    </th>
                    <th scope="col">
                      {t("client:client.tuya.controllers.table.th.deviceId")}
                    </th>
                    {/* <th scope="col">
                      {t("client:client.tuya.controllers.table.th.deviceType")}
                    </th> */}
                    <th scope="col">
                      {t("client:client.tuya.controllers.table.th.product")}
                    </th>
                    <th scope="col">
                      {t(
                        "client:client.tuya.controllers.table.th.outsideAccessMethods",
                      )}
                    </th>
                    <th scope="col">
                      {t(
                        "client:client.tuya.controllers.table.th.insideAccessMethods",
                      )}
                    </th>
                    <th scope="col">
                      {t("client:client.tuya.controllers.table.th.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!!tuyaControllers.length &&
                    tuyaControllers.map((item, index) => (
                      <TableRow
                        key={item.id}
                        item={item}
                        clientId={clientId}
                        index={index}
                      />
                    ))}
                  {!tuyaControllers.length && (
                    <th colSpan={7} scope="row" className="text-center">
                      {t("client:client.tuya.controllers.table.noControllers")}
                    </th>
                  )}
                </tbody>
              </Table>
              {!!tuyaControllers.length && (
                <div className="w-100">
                  <h6>
                    {t(
                      "client:client.tuya.controllers.modalCreate.accessMethod.outside.title",
                    )}{" "}
                    :
                  </h6>
                  <ul className="mb-4">
                    <li>
                      {t(
                        "client:client.tuya.controllers.modalCreate.accessMethod.outside.auto",
                      )}
                    </li>
                    <li>
                      {t(
                        "client:client.tuya.controllers.modalCreate.accessMethod.outside.manual",
                      )}
                    </li>
                  </ul>
                  <h6>
                    {t(
                      "client:client.tuya.controllers.modalCreate.accessMethod.inside.title",
                    )}{" "}
                    :
                  </h6>
                  <ul className="mb-0">
                    <li>
                      {t(
                        "client:client.tuya.controllers.modalCreate.accessMethod.inside.auto",
                      )}
                    </li>
                    <li>
                      {t(
                        "client:client.tuya.controllers.modalCreate.accessMethod.inside.manual",
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </form>
      </FormProvider>
      <Modal
        show={show}
        onHide={setShow}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("client:client.tuya.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.tuya.modal.body", {
            title: tuyaData?.tuya?.client?.title,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShow(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {!!tuyaId && (
        <CreateTuyaControllerModal
          show={showCreateController}
          setShow={setShowCreateController}
          tuyaId={tuyaId}
          clientId={clientId}
        />
      )}
    </div>
  );
});

const TuyaFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("tuya"), {
    context: {
      headers: {
        ClientId: clientId,
      },
    },
    variables: { id: clientId },
    fetchPolicy: "network-only",
  });

  const [updateTuya] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      { query: getClientFeatureQuery("tuya"), variables: { id: clientId } },
    ],
  });

  const tuyaEnabled = data?.client?.preferences?.tuya?.enabled ?? false;

  const onToggleTuya = useCallback(
    async (enabled: boolean) => {
      try {
        await updateTuya({
          variables: {
            input: {
              id: clientId,
              feature: "tuya",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "Tuya",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "Tuya",
          }),
        );
      }
    },
    [clientId, updateTuya, t],
  );

  if (!tuyaEnabled) {
    return (
      <div className="tab-pane fade show active" id="tuya" role="tabpanel">
        {isAdmin ? (
          <ToggleFeatureButton
            onClick={() => onToggleTuya(true)}
            featureName="Tuya"
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" id="tuya" role="tabpanel">
      <Tuya />
      {isAdmin && (
        <div className="mt-3 ml-3">
          <ToggleFeatureButton
            disable
            onClick={() => onToggleTuya(false)}
            featureName="Tuya"
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(TuyaFeature);
